import { AxiosResponse } from 'axios'

import { ConversationV2 } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/conversations'
import {
  ChatResponseV2,
  ListChatHistoryResponseV2,
  V2_CHAT_HISTORY_PAGE_SIZE,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/messagesV2'
import { GetOutcomesAction } from '@lyrahealth-inc/shared-app-logic/src/features/outcomes/types'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { CustomNotificationsPreferences } from '@lyrahealth-inc/shared-app-logic/src/models/notifications/Notifications'

import { axiosInstance } from '../axiosInstance'

export function getTokenData({ apptID, providerID, patientID, sessionNumber }: $TSFixMe) {
  return axiosInstance.post(
    `/lt/v1/video/session?appointment_id=${apptID}&provider_id=${providerID}&patient_id=${patientID}&session_number=${sessionNumber}`,
  )
}

export const getVideoSessionData = ({ apptId, patientId }: $TSFixMe) => {
  return axiosInstance.get(`/lt/v1/video/session?appointment_id=${apptId}&patientId=${patientId}`)
}

// temp for LTV dev
export function getDummyVideoAppointmentData(id: $TSFixMe, data: $TSFixMe) {
  return axiosInstance.post(`/v1/calendar/${id}/appointments/dummy`, data)
}

export function getAppointmentsByProviderData({ patientId, providerId, ...params }: $TSFixMe) {
  return axiosInstance.get(`/v2/calendar/${patientId}/${providerId}/appointments`, {
    params,
  })
}

export function getAppointmentsForClientData({ patientId, ...params }: $TSFixMe) {
  return axiosInstance.get(`/v2/calendar/${patientId}/appointments`, {
    params,
  })
}

export function getAssignmentsData({ patientId, providerId = '' }: $TSFixMe) {
  return axiosInstance.get('/lt/v1/assignments', {
    params: {
      patient_id: patientId,
      provider_id: providerId,
    },
  })
}

export const getNotificationPreferencesData = ({ lyraId }: $TSFixMe) => {
  return axiosInstance.get(`/lt/v1/preferences/notifications/${lyraId}`)
}

export const submitNotificationPreferencesData = ({ lyraId, data }: $TSFixMe) => {
  return axiosInstance.put(`/lt/v1/preferences/notifications/${lyraId}`, data)
}

export const getCustomNotificationsData = ({ lyraId }: { lyraId: string }) => {
  return axiosInstance.get(`/v1/user/${lyraId}/customNotificationSetting`)
}

export const submitCustomNotificationsData = ({
  lyraId,
  data,
}: {
  lyraId: string
  data: CustomNotificationsPreferences
}) => {
  return axiosInstance.put(`/v1/user/${lyraId}/customNotificationSetting`, data)
}

export const deleteCustomNotificationsData = ({ lyraId }: { lyraId: string }) => {
  return axiosInstance.delete(`/v1/user/${lyraId}/customNotificationSetting`)
}

export function getMessageConversationsData(providerId: string | null, clientId: string, types?: CONVERSATION_TYPE) {
  return axiosInstance.get(`/lt/v1/messaging/conversations`, {
    params: {
      provider_lyra_id: providerId,
      patient_lyra_id: clientId,
      ...(types ? { types } : {}),
    },
  })
}

export function getMessageConversationsV2Data(clientId: string) {
  return axiosInstance.get<ConversationV2[]>(`/lt/v2/messaging/conversations`, {
    params: {
      patient_id: clientId,
      types: CONVERSATION_TYPE.AI_COACH,
    },
  })
}

export function getAccessTokenData() {
  return axiosInstance.get(`/lt/v1/messaging/accessToken`)
}

export function getMessagesData(conversationId: $TSFixMe) {
  return axiosInstance.get(`/lt/v1/messaging/conversations/${conversationId}/messages`)
}

export function getMessagesV2Data(
  conversationId: string,
  before?: string,
): Promise<AxiosResponse<ListChatHistoryResponseV2>> {
  return axiosInstance.get<ListChatHistoryResponseV2>(`/lt/v2/messaging/conversations/${conversationId}/messages`, {
    params: {
      limit: V2_CHAT_HISTORY_PAGE_SIZE,
      ...(before ? { before } : {}),
    },
  })
}

export function getMessageData(conversationId: $TSFixMe, messageId: $TSFixMe) {
  return axiosInstance.get(`/lt/v1/messaging/conversations/${conversationId}/messages/${messageId}`)
}

export type SubmitMessageData = {
  body: string
  type: string
  client_id: string
  metadata?: Record<string, unknown> | null
}

export function submitMessageData(conversationId: string, data: SubmitMessageData) {
  return axiosInstance.post(`/lt/v1/messaging/conversations/${conversationId}/messages`, {
    message_body: data.body,
    message_type: data.type,
    message_author_id: data.client_id,
    message_author_type: 'patient',
    metadata: data.metadata,
  })
}

export function submitMessageV2Data(conversationId: string, data: SubmitMessageData) {
  return axiosInstance.post<ChatResponseV2>(`/lt/v2/messaging/conversations/${conversationId}/messages`, {
    message_body: data.body,
    message_author_id: data.client_id,
    message_author_type: 'patient',
    metadata: data.metadata,
  })
}

export function updateUnreadMessageCountData(conversationId: $TSFixMe, data: $TSFixMe) {
  return axiosInstance.patch(`/lt/v1/messaging/conversations/${conversationId}/attributes`, data)
}

export const getEpisodesData = ({ lyraId }: $TSFixMe) => {
  return axiosInstance.get(`/lt/v1/patient/${lyraId}/episodes`)
}

export function getChildEpisodesData(userLyraId: string, childLyraId: string) {
  return axiosInstance.get(`/v1/patient/${userLyraId}/child/${childLyraId}/episodes`)
}

export function getOutcomesData({ clientId, fields }: GetOutcomesAction) {
  return axiosInstance.get(`/v1/user/${clientId}/outcomes`, {
    params: {
      fields,
    },
  })
}

/**
 * get providers for user
 * @param clientId lyraId of the current user
 * @param includeChild set to true to get providers for current user and LCTT providers for current user's teens who they have booked for
 */
export function getProvidersData(clientId: string, includeChild?: boolean) {
  // This might be more appropriate for the appointmentDashboard file
  return axiosInstance.get(`/v1/patient/${clientId}/providers${includeChild ? '?includeChild=true' : ''}`)
}

export function ltNoop() {
  return axiosInstance.get('lt/v1/ping')
}

export const getFeedbackData = ({ lyraId, entityType }: { lyraId: string; entityType: string }) => {
  return axiosInstance.get(`/fdbks/v1/feedback?lyra_id=${lyraId}&entity_type=${entityType}`)
}
